import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Entry, PaymentSourceAccount } from '@models/account/model';
import { NgbDate, NgbDatepickerNavigateEvent } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { Model } from 'src/app/shared/models/clux/model-legacy';
import { BrandService } from '../../services/brand.service';
import { PaymentSourceAccountService } from '../../services/payment-source-account.service';
import { Dates } from '../../utils/dates';
import { MatButton } from '@angular/material/button';
import { FilterPopupComponent } from '../filter-popup/filter-popup.component';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatButton, FilterPopupComponent],
})
export class FilterComponent implements OnChanges, OnDestroy, OnInit {
  private brandService = inject(BrandService);
  private paymentSourceAccountService = inject(PaymentSourceAccountService);

  @Input() public category: Model.Category[];
  @Input() public activeObj: Model.FilterFlag;
  @Input() public maxEndDate: NgbDate;
  @Input() public calendarDatesToDisplay: string[];
  @Input() public useDatepickerDateRange: boolean = false;
  /**
   * Set to `true` when this filter is rendered inside a modal (Angular Material
   * `MatDialog`). Forwarded to app-filter-popup, where it controls whether the
   * ngb-datepicker calendar popups attach to `<body>` (default, normal pages) or
   * render inline (required inside dialogs — a CDK v22 dialog renders in the browser
   * top layer and would otherwise occlude a `<body>`-attached popup regardless of
   * z-index). See datepicker.util.ts for the full explanation.
   */
  @Input() public isRenderedInModal = false;
  @Output() public categoryFilterChanged = new EventEmitter<Model.FilterOption<Model.CategoryOptions[]>>();
  @Output() public dateFilterChanged = new EventEmitter<Model.FilterOption<Model.DateOptions>>();
  @Output() public selectedMonthData = new EventEmitter<NgbDatepickerNavigateEvent>();
  @Output() public filterClearedEvent = new EventEmitter<string>();

  @Input() public settlementEntries: Entry[];
  public categories: Model.Category[];
  public indexOfSelectedFilterButton: number;
  public dates: string[];
  public isDateSelected: boolean;
  private paymentSourceAccounts: PaymentSourceAccount[];
  private subscriptions = new Subscription();
  private readonly brandId = this.brandService.getBrand();

  public ngOnInit(): void {
    this.subscriptions.add(
      this.paymentSourceAccountService.getPaymentSourceAccounts().subscribe((paymentSourceAccounts) => {
        this.paymentSourceAccounts = paymentSourceAccounts;
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.category) {
      if (changes.category.currentValue) {
        this.categories = this.category;
      }
    }

    if (changes.activeObj) {
      if (changes.activeObj.currentValue.clearFilter) {
        this.categories.forEach((item) => {
          item.activeFlag = false;
          item.applyFlag = false;
          if (item.data) {
            item.data.map((e) => (e.checked = false));
            if (item.name === 'Benefit Plan') {
              item.data.map((e) => (e.isVisible = true));
            }
          }
        });
        this.isDateSelected = false;
        this.closePopup({ index: -1, active: false }, null);
      }
    }

    if (changes.calendarDatesToDisplay) {
      this.dates = changes.calendarDatesToDisplay.currentValue;
    }
  }

  public disableButton(item: Model.Category): boolean {
    return item.isDisabled || (item.name === 'Payment source' && !this.isDateSelected);
  }

  public openPopup($index: number, item: Model.Category): void {
    if (this.activeObj.active) {
      item.activeFlag = true;
    } else {
      item.activeFlag = false;
    }
    this.indexOfSelectedFilterButton = $index;
  }

  public closePopup(val: Model.FilterFlag, item: Model.Category): void {
    this.indexOfSelectedFilterButton = val.index;
    if (item) {
      const activeItem: Model.Category[] = this.categories.filter((iterator) => {
        return iterator.name.toLowerCase() === item.name.toLowerCase() && iterator.applyFlag === true;
      });

      if (this.activeObj.active && activeItem.length > 0) {
        if (val.active) {
          item.activeFlag = true;
        } else {
          item.activeFlag = false;
        }
      } else {
        this.deactivateFilter(item.name);
        item.activeFlag = false;
      }

      if (item.name === 'Date') {
        this.isDateSelected = item.applyFlag ? true : false;
        if (this.settlementEntries) {
          this.hideAllPaymentSources();
          this.showPaymentSourcesAssociatedWithEntries();
        }
      }
      if (item.name === 'Payment source' && !item.applyFlag) {
        this.categories.forEach((category) => {
          if (category.name === 'Payment source') {
            if (category.data instanceof Array) {
              category.data.map((option) => {
                option.checked = false;
                if (option.bankAccounts) {
                  option.bankAccounts[0].checked = false;
                }
                return option;
              });
            }
            category.activeFlag = false;
          }
        });
      }
    }
  }

  public categoryDirtyChange(event: Model.FilterOption<Model.CategoryOptions[]>): void {
    this.categories.forEach((iterator) => {
      if (event.name) {
        if (iterator.name.toLowerCase() === event.name.toLowerCase()) {
          iterator.applyFlag = true;
        }
      }
    });
    this.categoryFilterChanged.emit(event);
  }

  public dateDirtyChange(event: Model.FilterOption<Model.DateOptions>): void {
    this.categories.forEach((iterator) => {
      if (event.name) {
        if (iterator.name.toLowerCase() === event.name.toLowerCase()) {
          iterator.applyFlag = true;
        }
      }
    });
    this.dateFilterChanged.emit(event);
  }

  public filterCleared(event: Model.EmitSelected): void {
    if (event.name.toLowerCase() === 'date') {
      this.categories.forEach((category) => {
        if (category.name === 'Payment source') {
          if (category.data instanceof Array) {
            category.data.map((option) => {
              option.checked = false;
              if (option.bankAccounts) {
                option.bankAccounts[0].checked = false;
              }
              return option;
            });
          }
          category.activeFlag = false;
        }
      });
    }

    const eventCategory = this.categories.find((category) => category.name.toLowerCase() === event.name.toLowerCase());
    if (eventCategory && eventCategory.isMultiOptionSelect) {
      this.categories.forEach((category) => {
        if (category.name === eventCategory.name && category.data instanceof Array) {
          category.data.map((option) => {
            option.checked = false;
            return option;
          });
        }
      });
    }

    const fakeEvent = {
      name: event.name,
    };
    this.categoryDirtyChange(fakeEvent);
    this.dateDirtyChange(fakeEvent);

    this.filterClearedEvent.emit(event.name);
  }

  public selectedMonths(event: NgbDatepickerNavigateEvent): void {
    this.selectedMonthData.emit(event);
  }

  /**
   * This method is use to deactivate the active filter
   * @param name is the selected filter name
   */
  private deactivateFilter(name: string): void {
    switch (name) {
      case 'Benefit Plan':
        sessionStorage.removeItem('benefitPlan');
        break;
      case 'Payment Plan':
        sessionStorage.removeItem('paymentPlan');
        break;
      case 'Division':
        sessionStorage.removeItem('division');
        break;
      case 'Class':
        sessionStorage.removeItem('class');
        break;
    }
  }

  private hideAllPaymentSources(): void {
    this.categories
      .find((category) => category.name === 'Payment source')
      ?.data?.forEach((categoryOption) => {
        categoryOption.isVisible = false;
        categoryOption.bankAccounts?.forEach((bankAccount) => (bankAccount.isVisible = false));
      });
  }

  private showPaymentSourcesAssociatedWithEntries(): void {
    if (!this.settlementEntries?.length) {
      throw new Error('Expected at least one settlment entry');
    }
    const selectedDate = dayjs.utc(sessionStorage.getItem('fromDate')).format('YYYY-MM-DD');
    this.settlementEntries
      .filter((settlementEntry) => Dates.isSame(settlementEntry.transactionDateTime, selectedDate))
      .forEach((settlementEntry) => {
        // Find the UI element and show it.
        const paymentSourceUIOption = this.categories
          .find((c) => c.name === 'Payment source')
          ?.data?.find((categoryOption) => {
            if (categoryOption.ids?.includes(settlementEntry.paymentSourceId)) {
              return categoryOption;
            }
            // We didn't match on Wire or Paper Check options; see if it matches a particular bank account.
            return categoryOption.bankAccounts?.find((bankAccount) => {
              const paymentSourceForSettlementEntry = this.paymentSourceAccounts.find((psa) => psa.id === settlementEntry.paymentSourceId);
              if (bankAccount.id === paymentSourceForSettlementEntry?.paymentAccountId) {
                bankAccount.isVisible = true; // Make the bank account visible. Below we'll make the parent ACH option visible.
                return true;
              }
              return false;
            });
          });

        if (paymentSourceUIOption) {
          paymentSourceUIOption.isVisible = true;
        }
      });
  }
}
